<script setup>
import Layout from "@/layouts/main.vue";
import PageHeader from "@/components/page-header";
import '@jobinsjp/vue3-datatable/dist/style.css';
import { DataTable, TableBody, TableHead, TableBodyCell } from '@jobinsjp/vue3-datatable';
import moment from 'moment';
import { truncate } from 'lodash';
import Swal from "sweetalert2";
import { computed, ref } from 'vue';
import { useStore } from 'vuex';

const store = useStore();
const loading = computed(() => store.state.globals.loading);
const users = computed(() => store.state.user.users);
const pagination = computed(() => store.state.user.pagination);
const title = ref("Users List");
const items = ref([
  {
      text: "Home",
      href: "/"
    },
      {
      text: "User Management",
    },
    {
      text: "Users List",
      active: true,
    }
])

const query = ref({
    search: ''
})

const getAllUsers = async (dt) => {
  await store.dispatch('user/getUsers', {
    page: dt.page,
    size: dt.per_page,
    sortColumn: 'updated_at',
    search: dt.search,
    orderBy: 'desc'
  });
};

function standardDate(date) {
  if (!date) return 'N/A';
  return moment(date).format('YYYY-MM-DD h:mm A')
}

function getUserRoles(values) {
  if (values.length > 0) {
    let roles = [];
    values.forEach((value) => {
      roles.push(value.name);
    })

    return roles.join(', ');
  }
  return 'N/A';
}

function getUserLGUs(values) {
  if (values.length > 0) {
    let lgus = [];
    values.forEach((value) => {
      lgus.push(value.name);
    })

    return lgus.join(', ');
  }
  return 'N/A';
}

const deactivateUser = async (id, name) => {
  Swal.fire({
    title: "Are you sure?",
    text: `You want to deactivate ${name} ?`,
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#34c38f",
    cancelButtonColor: "#f46a6a",
    confirmButtonText: "Deactivate",
  }).then(async (result) => {
    if (result.value) {
      await store.dispatch('user/deactivateUser', { id, name });
    }
  });
}

const activateUser = async (id, name) => {
  Swal.fire({
    title: "Are you sure?",
    text: `You want to activate ${name} ?`,
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#34c38f",
    cancelButtonColor: "#f46a6a",
    confirmButtonText: "Activate",
  }).then(async (result) => {
    if (result.value) {
      await store.dispatch('user/activateUser', { id, name });
    }
  });
}
</script>

<template>
  <Layout>
      <PageHeader :title="title" :items="items" />
       <b-overlay :show="loading">
          <div class="d-flex mb-4 justify-content-between">
            <div>
              <h1 class="fs-4 text">Users List</h1>
              <small>Lists of all users</small>
            </div>
            <div>
              <router-link
                  to="/user-management/users/create"
                  class="btn btn-primary right"
                >
                  <i class="ri-add-fill label-icon align-middle fs-16 me-2"></i>
                  New User
              </router-link>
            </div>
          </div>

          <div class="row">
            <div class="col lg-12">
                <data-table :rows="users"
                    :pagination="pagination"
                    striped
                    :query="query"
                    filter
                    :loading="loading"
                    hoverable
                    sortable
                    @loadData="getAllUsers"
                >
                  <template #thead>
                      <table-head>Name</table-head>
                      <table-head>Email/Username</table-head>
                      <table-head>Status</table-head>
                      <table-head>Role</table-head>
                      <table-head>LGU</table-head>
                      <table-head>Last Login</table-head>
                      <table-head>Last Activity</table-head>
                      <table-head>Created By</table-head>
                      <table-head>Date Modified</table-head>
                      <table-head>Action</table-head>
                  </template>

              <template #tbody="{row}">
                  <table-body>
                    <router-link :title="row.name" :to="`/user-management/users/${row.id}/view`">
                      {{ truncate(row.name, { length: 24 }) }}
                    </router-link>
                  </table-body>
                  <table-body :title="row.email ? row.email : row.username" v-text="truncate(row.email ? row.email : row.username, { length: 24 })"/>
                  <table-body>
                    <b-badge :variant="row.is_active === 1 ? 'success' : row.is_active === 2 ? 'warning' : 'danger'">
                      {{ row.is_active === 1 ? 'Active' : row.is_active === 2 ? 'Pending' : 'Inactive' }}
                    </b-badge>
                  </table-body>
                   <table-body :title="getUserRoles(row.roles)">
                      {{ truncate(getUserRoles(row.roles), { length: 20 }) }}
                  </table-body>
                  <table-body :title="getUserRoles(row.lgus)">
                      {{ truncate(getUserLGUs(row.lgus), { length: 20 }) }}
                  </table-body>
                 <table-body>
                    {{ standardDate(row.last_login_at) }}
                  </table-body>
                  <table-body>
                    {{ standardDate(row.last_activity_at) }}
                  </table-body>
                  <table-body>
                    {{ row.created_by_data && row.created_by_data.name ? row.created_by_data.name : 'N/A' }}
                  </table-body>
                  <table-body>
                    {{ standardDate(row.updated_at) }}
                  </table-body>
                  <table-body>
                     <router-link
                        :to="`/user-management/users/${row.id}/view`"
                        class="btn btn-secondary mr-1"
                        title="View"
                      >
                      <i class="ri-eye-line label-icon align-middle"></i>
                    </router-link>
                     <router-link
                        :to="`/user-management/users/${row.id}/edit`"
                        class="btn btn-info mr-1"
                        title="Edit"
                      >
                      <i class="ri-pencil-line label-icon align-middle"></i>
                    </router-link>
                    <button
                      @click="row.is_active ? deactivateUser(row.id, row.name) : activateUser(row.id, row.name)"
                      :class="row.is_active ? 'btn-danger' : 'btn-success'"
                      class="btn"
                      :title="row.is_active ? 'Deactivate' : 'Activate'"
                    >
                      <i
                        :class="row.is_active ? 'mdi-account-off' : 'mdi-account-check'"
                        class="mdi label-icon align-middle"></i>
                    </button>
                  </table-body>
              </template>

                  <template #empty>
                    <TableBodyCell colspan="10">
                        No user found.
                    </TableBodyCell>
                </template>
              </data-table>
            </div>
          </div>
      </b-overlay>
  </Layout>
</template>
